import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("omnicare_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("omnicare_token");
      localStorage.removeItem("omnicare_user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ============ AUTH ============
export const authAPI = {
  login: (email, password) =>
    api.post("/auth/login", new URLSearchParams({ username: email, password }), {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }),
  register: (data) => api.post("/auth/register", data),
  me: () => api.get("/auth/me"),
  listUsers: () => api.get("/auth/users"),
};

// ============ ANALYTICS ============
export const analyticsAPI = {
  dashboard: (params) => api.get("/analytics/dashboard", { params }),
  nlQuery: (question) => api.post("/analytics/nlq", { question }),
  issueMining: (params) => api.get("/analytics/issues", { params }),
  conversations: (params) => api.get("/analytics/conversations", { params }),
  conversationDetail: (id) => api.get(`/analytics/conversations/${id}`),
  heartbeat: (params) => api.get("/analytics/heartbeat", { params }),
  returnVisits: (params) => api.get("/analytics/return-visits", { params }),
  upload: (formData) =>
    api.post("/analytics/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

// ============ EMPLOYEES ============
export const employeesAPI = {
  list: () => api.get("/employees/"),
  leaderboard: (params) => api.get("/employees/leaderboard", { params }),
  profile: (agentId, params) => api.get(`/employees/${agentId}/profile`, { params }),
  evaluations: (agentId, params) => api.get(`/employees/${agentId}/evaluations`, { params }),
  trend: (agentId, params) => api.get(`/employees/${agentId}/trend`, { params }),
  improvements: (agentId, params) => api.get(`/employees/${agentId}/improvements`, { params }),
  periodReviews: (agentId, params) => api.get(`/employees/${agentId}/period-reviews`, { params }),
};

// ============ CHURN ============
export const churnAPI = {
  highRisk: () => api.get("/churn/high-risk"),
  detail: (customerId) => api.get(`/churn/${customerId}`),
};

// ============ AI COACH ============
export const coachAPI = {
  suggest: (conversationId) => api.post("/coach/suggest", { conversation_id: conversationId }),
  alerts: () => api.get("/coach/alerts"),
};

// ============ WEBSOCKET ============
// Derive the backend origin (without /api path) for WebSocket connections
const BACKEND_ORIGIN = (() => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && /^https?:\/\//.test(envUrl)) {
    return envUrl.replace(/\/api\/?$/, ""); // "http://localhost:8000/api" → "http://localhost:8000"
  }
  return "http://localhost:8000"; // hard fallback
})();

export const createWebSocket = (channel = "dashboard") => {
  const token = localStorage.getItem("omnicare_token");
  if (!token) return null; // don't attempt connection without auth

  const wsUrl = BACKEND_ORIGIN.replace(/^http/, "ws") + `/ws/${channel}?token=${token}`;
  return new WebSocket(wsUrl);
};

export default api;
